import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import { useLocalization } from '@progress/kendo-react-intl';
import { Splitter, SplitterOnChangeEvent, SplitterPane, SplitterPaneProps } from '@progress/kendo-react-layout';
import { useQuery } from 'react-query';
import { EndpointDetailsDto } from '../../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import LoadingSpinner from '../../../../../../Shared/Components/LoadingSpinner';
import { AnomalyBandProvider } from './AnomalyBand/AnomalyBandContext';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { DEFAULT_ANOMALY_FORM_VALUES } from './AnomalyBand/types';
import AnomalyDetectorButtons from './AnomalyDetectorButtons';
import AnomalyDetectorForm from './AnomalyDetectorForm';
import anomalyApiService from '../../../../Utilities/AnomalyApiService';
import useIdentityStore from '../../../../../../../Zustand/identityStore';
import { enMessages } from '../../../../../../../messages/en-US';

interface Props {
  assetId?: string;
  selectedEndpoint?: EndpointDetailsDto;
  /** Set instead of selectedEndpoint when the bands belong to a device (e.g. gateway). */
  deviceId?: string;
  eventFilter: { startDate: Date; endDate: Date };
  onClose?: () => void;
  containerHeight: number;
  legendVisible?: boolean;
  onLegendToggle?: () => void;
  children: (props: { dialogHeight: number }) => React.ReactNode;
  /** When provided, use these bands instead of fetching from API (e.g. from Insights table) */
  bandsOverride?: AnomalyBandWithForm[];
  /** Pre-select this band ID when using bandsOverride */
  preselectedBandId?: string;
  /** When true, hide Add/Delete anomaly buttons (read-only view from Insights) */
  hideAnomalyButtons?: boolean;
  /** When true, disable Add/Delete anomaly buttons (e.g. no chart data for the selected range) */
  disableAnomalyButtons?: boolean;
  keepSingleBandSelected?: boolean;
  leftPaneSize?: string;
}

/** Reusable anomaly detector content (provider + layout). Use inside Dialog or Window. */
const AnomalyDetectorContent: React.FC<Props> = ({
  assetId,
  selectedEndpoint,
  deviceId,
  eventFilter,
  onClose,
  containerHeight,
  legendVisible,
  onLegendToggle,
  children,
  bandsOverride,
  preselectedBandId,
  hideAnomalyButtons = false,
  disableAnomalyButtons = false,
  keepSingleBandSelected = false,
  leftPaneSize = '70%',
}) => {
  const localizationService = useLocalization();
  const targetId = deviceId || selectedEndpoint?.id || '';
  const user = useIdentityStore((store) => store.user);
  const userId = user?.profile?.sub ?? undefined;
  const userName = user?.profile?.preferred_username ?? undefined;
  const [panes, setPanes] = useState<SplitterPaneProps[]>([
    { size: leftPaneSize, min: '320px', collapsible: false },
    { min: '320px', collapsible: false },
  ]);

  const { data: fetchedBands = [], isLoading: isLoadingBands } = useQuery<AnomalyBandWithForm[]>(
    ['endpointAnomalyBands', targetId],
    () => (deviceId ? anomalyApiService.getDeviceAnomalies(deviceId) : anomalyApiService.getPointAnomalies(targetId)),
    { enabled: !!targetId && !bandsOverride }
  );

  const initialBands = useMemo(
    (): AnomalyBandWithForm[] =>
      bandsOverride && bandsOverride.length > 0
        ? bandsOverride
        : fetchedBands.map((b) => ({
        id: b.id,
        bandType: b.bandType,
        persistedId: b.persistedId,
        anomalyId: b.anomalyId,
        notes: b.notes ?? DEFAULT_ANOMALY_FORM_VALUES.notes,
        pendingNotes: b.pendingNotes ?? DEFAULT_ANOMALY_FORM_VALUES.pendingNotes,
        revisionHistory: b.revisionHistory ?? DEFAULT_ANOMALY_FORM_VALUES.revisionHistory,
        startTime: b.startTime,
        endTime: b.endTime,
        streams: b.streams ?? DEFAULT_ANOMALY_FORM_VALUES.streams,
        criticality: b.criticality ?? DEFAULT_ANOMALY_FORM_VALUES.criticality,
        indicators: b.indicators ?? DEFAULT_ANOMALY_FORM_VALUES.indicators,
        rootCauses: b.rootCauses ?? DEFAULT_ANOMALY_FORM_VALUES.rootCauses,
        actions: b.actions ?? DEFAULT_ANOMALY_FORM_VALUES.actions,
        mlResults: b.mlResults ?? DEFAULT_ANOMALY_FORM_VALUES.mlResults,
        confidence: b.confidence ?? DEFAULT_ANOMALY_FORM_VALUES.confidence,
        savings: b.savings ?? DEFAULT_ANOMALY_FORM_VALUES.savings,
        ongoing: b.ongoing ?? DEFAULT_ANOMALY_FORM_VALUES.ongoing,
        userNotes: b.userNotes ?? DEFAULT_ANOMALY_FORM_VALUES.userNotes,
        createdByUserId: b.createdByUserId ?? DEFAULT_ANOMALY_FORM_VALUES.createdByUserId,
        createdByUserName: b.createdByUserName ?? DEFAULT_ANOMALY_FORM_VALUES.createdByUserName,
      })),
    [fetchedBands, bandsOverride]
  );

  const handleSplitterChange = (event: SplitterOnChangeEvent) => {
    setPanes(event.newState);
  };

  useEffect(() => {
    setPanes([
      { size: leftPaneSize, min: '320px', collapsible: false },
      { min: '320px', collapsible: false },
    ]);
  }, [leftPaneSize]);

  if (!bandsOverride && isLoadingBands) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <AnomalyBandProvider
      targetId={targetId}
      initialBands={initialBands}
      initialSelectedBandId={preselectedBandId}
      keepSingleBandSelected={keepSingleBandSelected}
    >
      <Splitter
        panes={panes}
        onChange={handleSplitterChange}
        style={{ height: '100%', minHeight: 400, width: '100%', border: 'none' }}
      >
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <div className="flex h-full min-h-0 flex-col overflow-hidden pr-4">
            {(!hideAnomalyButtons || !!onLegendToggle) && (
              <div className="mb-2 flex flex-wrap items-center gap-2 [&>button]:shrink-0">
                {!hideAnomalyButtons && (
                  <AnomalyDetectorButtons
                    eventFilter={eventFilter}
                    userId={userId}
                    userName={userName}
                    legendVisible={legendVisible}
                    onLegendToggle={onLegendToggle}
                    disabled={disableAnomalyButtons}
                  />
                )}
                {hideAnomalyButtons && onLegendToggle && (
                  <Button
                    togglable
                    selected={legendVisible}
                    onClick={onLegendToggle}
                    title={localizationService.toLanguageString('custom.legend', enMessages.custom.legend)}
                  >
                    <FontAwesomeIcon icon={faListUl} />
                  </Button>
                )}
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-hidden">
              {children({ dialogHeight: containerHeight })}
            </div>
          </div>
        </SplitterPane>
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <div className="flex h-full min-h-0 flex-col overflow-hidden border-l border-gray-200">
            <AnomalyDetectorForm
              assetId={assetId ?? ''}
              endpointId={selectedEndpoint?.id ?? ''}
              deviceId={deviceId}
              onClose={onClose ?? (() => {})}
              userId={userId}
              userName={userName}
            />
          </div>
        </SplitterPane>
      </Splitter>
    </AnomalyBandProvider>
  );
};

export default AnomalyDetectorContent;
